import React from "react";
import { Link } from "react-router-dom";

/**
 * Generic Breadcrumb Component for Support Pages
 *
 * @param {Object} props
 * @param {Array} props.items - Array of breadcrumb items
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.showHome - Whether to show home link
 * @param {string} props.homeLabel - Label for home link (default: "Home")
 * @param {string} props.homePath - Path for home link (default: "/")
 */
const SupportBreadcrumb = ({
  items = [],
  className = "",
  showHome = true,
  homeLabel = "Home",
  homePath = "/",
}) => {
  // Function to render a single breadcrumb item
  const renderBreadcrumbItem = (item, index, isLast = false) => {
    const { label, path } = item;

    return (
      <li key={index} className="inline-flex items-center min-w-0">
        {index > 0 && (
          <svg
            className="rtl:rotate-180 w-3 h-3 text-brand-mist mx-1.5 md:mx-2 flex-shrink-0"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="m1 9 4-4-4-4"
            />
          </svg>
        )}
        {path && !isLast ? (
          <Link
            to={path}
            className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-gold-ink transition-colors duration-150 ease-in-out truncate max-w-[100px] sm:max-w-[150px] md:max-w-none"
            aria-current={isLast ? "page" : undefined}
            title={label}
          >
            {label}
          </Link>
        ) : (
          <span
            className="text-sm font-medium text-brand-charcoal truncate max-w-[100px] sm:max-w-[150px] md:max-w-none"
            aria-current="page"
            title={label}
          >
            {label}
          </span>
        )}
      </li>
    );
  };

  // Function to render mobile breadcrumb (last 3 items with dots)
  const renderMobileBreadcrumb = () => {
    const allItems = showHome
      ? [{ label: homeLabel, path: homePath }, ...items]
      : items;

    if (allItems.length <= 3) {
      // If 3 or fewer items, show all
      return allItems.map((item, index) =>
        renderBreadcrumbItem(item, index, index === allItems.length - 1)
      );
    }

    // For 4+ items, show dots + last 3 items
    const lastThreeItems = allItems.slice(-3);

    return (
      <>
        {/* Dots to indicate hidden items */}
        <li className="inline-flex items-center">
          <span className="text-sm text-brand-mist px-1">•••</span>
        </li>

        {/* Last 3 items */}
        {lastThreeItems.map((item, index) => {
          const actualIndex = allItems.length - 3 + index;
          return renderBreadcrumbItem(
            item,
            actualIndex,
            actualIndex === allItems.length - 1
          );
        })}
      </>
    );
  };

  const allItems = showHome
    ? [{ label: homeLabel, path: homePath }, ...items]
    : items;

  if (allItems.length === 0) {
    return null;
  }

  return (
    <nav
      className={`flex items-center justify-center px-3 py-1.5 max-w-full overflow-hidden ${className}`}
      aria-label="Breadcrumb"
    >
      {/* Mobile view (last 3 items with dots) */}
      <ol className="inline-flex items-center space-x-1.5 md:space-x-2 rtl:space-x-reverse md:hidden min-w-0">
        {renderMobileBreadcrumb()}
      </ol>

      {/* Desktop view (full) */}
      <ol className="hidden md:inline-flex items-center space-x-2 rtl:space-x-reverse min-w-0">
        {allItems.map((item, index) =>
          renderBreadcrumbItem(item, index, index === allItems.length - 1)
        )}
      </ol>
    </nav>
  );
};

export default SupportBreadcrumb;
